/// <reference types="google.maps" />

import type {
  Language,
  LatLngLiteral,
  MapLocatorItemType,
  MapLocatorPopoverSettings,
  MapLocatorTag
} from 'ecosystem'
import { getDocumentLanguage, normalizeCountryCode, resolveCountryName } from 'shared-utils'
import { DEFAULT_MARKER_COLOR } from './constants'

const EARTH_RADIUS_KM = 6371

const toRadians = (value: number) => (value * Math.PI) / 180

/** Great-circle distance in kilometres. Accurate enough for a store locator. */
export const distanceInKm = (from: LatLngLiteral, to: LatLngLiteral): number => {
  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const lat1 = toRadians(from.lat)
  const lat2 = toRadians(to.lat)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2)

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const formatDistance = (km: number, unit: string): string =>
  `${km < 10 ? km.toFixed(1) : Math.round(km)} ${unit}`

/** A point is renderable only when it carries usable coordinates. */
export const isPlottable = (item: MapLocatorItemType): boolean =>
  Number.isFinite(item.lat) &&
  Number.isFinite(item.lng) &&
  Math.abs(item.lat) <= 90 &&
  Math.abs(item.lng) <= 180

export const itemPosition = (item: MapLocatorItemType): LatLngLiteral => ({
  lat: item.lat,
  lng: item.lng
})

/** ISO code normalised and localised: "SE" -> "Sverige" in sv, "Sweden" in en. */
export const countryLabel = (item: MapLocatorItemType, language?: Language): string =>
  resolveCountryName(item.country, language ?? getDocumentLanguage()) ?? ''

/** Postal code and city on one line, as addresses are conventionally written. */
export const postalCity = (item: MapLocatorItemType): string =>
  [item.postalCode, item.city].filter(Boolean).join(' ')

export const fullAddress = (item: MapLocatorItemType, language?: Language): string =>
  [item.address, postalCity(item), countryLabel(item, language)].filter(Boolean).join(', ')

/**
 * Folds case and diacritics so a visitor typing "Malmo" or "Goteborg" still
 * matches "Malmö" and "Göteborg". NFD handles the combining marks; the letters
 * that do not decompose (ø, æ, ß, đ) are mapped explicitly.
 */
const NON_DECOMPOSING: Record<string, string> = {
  ø: 'o',
  æ: 'ae',
  œ: 'oe',
  ß: 'ss',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ł: 'l'
}

export const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[øæœßđðþł]/g, (char) => NON_DECOMPOSING[char] as string)

export const itemSearchIndex = (item: MapLocatorItemType): string =>
  normalizeText(
    [item.name, item.address, item.city, item.postalCode, item.country].filter(Boolean).join(' ')
  )

export const tagColor = (tags: MapLocatorTag[], tagId?: string): string =>
  tags.find((tag) => tag.id === tagId)?.color || DEFAULT_MARKER_COLOR

/** Resolves the marker visual for a point: per-tag icon wins over the widget default. */
export const resolveMarkerIcon = (
  item: MapLocatorItemType,
  tags: MapLocatorTag[],
  defaultIconUrl?: string
): string | undefined => {
  const taggedIcon = item.tagIds
    ?.map((id) => tags.find((tag) => tag.id === id)?.iconUrl)
    .find(Boolean)

  return taggedIcon || defaultIconUrl
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}

export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] as string)

/** Only http(s), mailto and tel survive — everything else (javascript:, data:) is dropped. */
const safeUrl = (value: string): string | null => {
  const trimmed = value.trim()

  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) {
    return trimmed
  }

  // Bare domains typed by merchants — "example.com" — are treated as https
  if (/^[\w-]+(\.[\w-]+)+([/?#].*)?$/.test(trimmed)) {
    return `https://${trimmed}`
  }

  return null
}

const link = (href: string, text: string): string => {
  const url = safeUrl(href)

  return url
    ? `<a href="${escapeHtml(url)}" rel="noopener noreferrer" target="_blank">${escapeHtml(text)}</a>`
    : escapeHtml(text)
}

/**
 * The same pill the results list draws with a Chakra `Badge`, as markup: the
 * popover is an HTML template, so it cannot mount a component. Colours are
 * merchant-editable, hence the same character filter the marker artwork uses.
 */
const safeColor = (value: string): string => value.trim().replace(/[^#\w(),.%\s-]/g, '')

const BADGE_BASE =
  'display:inline-block;margin:0 0.25rem 0.25rem 0;padding:0 0.25rem;border-radius:9999px;' +
  'font-size:xx-small;font-weight:700;line-height:1.5;text-transform:uppercase;white-space:nowrap'

const tagBadge = (tag: MapLocatorTag): string => {
  const color = tag.color ? safeColor(tag.color) : ''
  // Chakra's default subtle grey when the tag carries no colour of its own
  const palette = color ? `background:${color};color:#FFFFFF` : 'background:#EDF2F7;color:#1A202C'

  return `<span style="${BADGE_BASE};${palette}">${escapeHtml(tag.label)}</span>`
}

export interface PopoverTemplateContext {
  item: MapLocatorItemType
  tags: MapLocatorTag[]
  distanceLabel?: string
  /** Defaults to the document language; supplied explicitly by tests. */
  language?: Language
}

/**
 * Placeholder values are HTML-escaped before substitution, so merchant-authored
 * templates can only inject markup they wrote themselves. `ParsedHtml` sanitises
 * the result again on render.
 */
const templateValues = ({
  item,
  tags,
  distanceLabel,
  language
}: PopoverTemplateContext): Record<string, string> => ({
  name: escapeHtml(item.name || ''),
  address: escapeHtml(item.address || ''),
  city: escapeHtml(item.city || ''),
  postalCode: escapeHtml(item.postalCode || ''),
  postalCity: escapeHtml(postalCity(item)),
  // The readable name, since that is what an address line wants
  country: escapeHtml(countryLabel(item, language)),
  // The raw ISO code stays available for flags, filtering or compact layouts
  countryCode: escapeHtml(item.country ? normalizeCountryCode(item.country) : ''),
  fullAddress: escapeHtml(fullAddress(item, language)),
  phone: item.phone ? link(`tel:${item.phone.replace(/\s/g, '')}`, item.phone) : '',
  email: item.email ? link(`mailto:${item.email}`, item.email) : '',
  website: item.website ? link(item.website, item.website.replace(/^https?:\/\//i, '')) : '',
  image: item.imageUrl
    ? `<img alt="${escapeHtml(item.name || '')}" src="${escapeHtml(item.imageUrl)}" />`
    : '',
  tags: (item.tagIds || [])
    .map((id) => tags.find((tag) => tag.id === id))
    .filter((tag): tag is MapLocatorTag => Boolean(tag?.label))
    .map(tagBadge)
    .join(''),
  distance: distanceLabel ? escapeHtml(distanceLabel) : '',
  // Authored markup from the rich-text editor, inserted as-is. `ParsedHtml`
  // sanitises the assembled result, and placeholders inside it are not
  // re-processed, so braces in merchant content are harmless.
  content: item.contentHtml || ''
})

/**
 * `\s*` also has to absorb `&nbsp;`: a template pasted out of a rich-text editor
 * comes back with non-breaking spaces inside the braces, which would otherwise
 * make the placeholder silently render as literal text.
 */
const PLACEHOLDER = /\{\{(?:\s|&nbsp;)*(\w+)(?:\s|&nbsp;)*\}\}/g

export const renderTemplate = (template: string, context: PopoverTemplateContext): string => {
  const values = templateValues(context)

  // A replacer function is required, not a replacement string: values may contain
  // `$&`-style sequences that String.replace would otherwise expand.
  return template.replace(PLACEHOLDER, (match, key: string) =>
    key in values ? (values[key] as string) : match
  )
}

/** Template used when the merchant has not authored one — driven by `popover.fields`. */
export const buildDefaultTemplate = (popover: MapLocatorPopoverSettings): string => {
  const { fields = {} } = popover

  return [
    fields.image ? '{{image}}' : '',
    '<strong>{{name}}</strong>',
    fields.address ? '<p>{{fullAddress}}</p>' : '',
    fields.phone ? '<p>{{phone}}</p>' : '',
    fields.email ? '<p>{{email}}</p>' : '',
    fields.website ? '<p>{{website}}</p>' : '',
    fields.content ? '{{content}}' : '',
    // Last: the badges close the card, as they do in a results-list row
    fields.tags ? '<p>{{tags}}</p>' : ''
  ]
    .filter(Boolean)
    .join('')
}

/**
 * A point's own `popoverHtml` replaces the shared *template*, not the data: it
 * goes through the same interpolation, so every placeholder — including
 * `{{content}}` — still resolves. Empty placeholder rows ("<p></p>") left by
 * unset fields are dropped so the card stays tight.
 */
export const popoverContent = (
  popover: MapLocatorPopoverSettings,
  context: PopoverTemplateContext
): string => {
  const template =
    context.item.popoverHtml?.trim() || popover.template?.trim() || buildDefaultTemplate(popover)

  return renderTemplate(template, context)
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<small>\s*<\/small>/g, '')
}

export const interpolateCount = (template: string, count: number): string =>
  template.replace(/\{\s*count\s*\}/g, String(count))

/** Longitude comparison that survives bounds crossing the antimeridian. */
export const isWithinBounds = (
  { lat, lng }: LatLngLiteral,
  bounds: google.maps.LatLngBoundsLiteral
): boolean => {
  if (lat < bounds.south || lat > bounds.north) {
    return false
  }

  return bounds.west <= bounds.east
    ? lng >= bounds.west && lng <= bounds.east
    : lng >= bounds.west || lng <= bounds.east
}
